import { useForm } from 'react-hook-form';
import { useAddSource } from '@/features/sources/hooks/useAddSource';
import {
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Code,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  Input,
  Text,
} from '@/ui';
import { getErrorMessage } from '@/utils/errors';

type Props = {
  onBack: () => void;
  onAdded: (feedId: string) => void;
};

type FormValues = {
  url: string;
};

export const IcsFeedSource = ({ onBack, onAdded }: Props) => {
  const {
    formState: { errors },
    handleSubmit: onSubmit,
    register,
  } = useForm<FormValues>({ defaultValues: { url: '' } });
  const { addSource, isPending, error } = useAddSource();
  const toast = useToast();

  const handleSubmit = ({ url }: FormValues) => {
    addSource(url, {
      onSuccess: (data) => {
        toast.notify('Calendar added');
        onAdded(data.addSourceToExperience.id);
      },
    });
  };

  return (
    <Box maxW="540px">
      <Flex align="center" gap={3} mb={6}>
        <Button
          color="ink.soft"
          flexShrink={0}
          h="auto"
          minW={0}
          p={0}
          variant="link"
          onClick={onBack}
        >
          <Icon icon="ArrowLeft" size={4} />
        </Button>
        <Text
          color="ink.strong"
          fontSize="26px"
          fontWeight={700}
          letterSpacing="-0.02em"
        >
          Paste a calendar link
        </Text>
      </Flex>

      <form noValidate onSubmit={onSubmit(handleSubmit)}>
        <Text color="ink.soft" fontSize="ah-control" mb={4}>
          Enter the URL to an <Code colorScheme="gray">.ics</Code> calendar
          feed:
        </Text>

        {error && (
          <Alert mb={5} status="error">
            <AlertIcon />
            <AlertTitle>{getErrorMessage(error)}</AlertTitle>
          </Alert>
        )}

        <FormControl isInvalid={!!errors.url} mb={5}>
          <Input
            {...register('url', {
              pattern: {
                message: 'Please enter a valid feed URL',
                value: /:\/\//,
              },
              required: 'Please enter a URL',
            })}
            autoFocus
            placeholder="https://example.com/calendar.ics"
            type="url"
          />
          <FormErrorMessage>{errors.url?.message}</FormErrorMessage>
        </FormControl>

        <Flex justify="flex-end">
          <Button isLoading={isPending} type="submit">
            Add source
          </Button>
        </Flex>
      </form>
    </Box>
  );
};
